import { createColumnHelper } from "@tanstack/react-table";
import saveAs from "file-saver";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { Can, ReactTable } from "components";
import { useRegisterNavAction } from "hooks";
import { FACTORYEXPORT_FREQUENCY, FACTORYEXPORT_QUERYRANGE } from "models";
import { DeleteExportModal, ExportLogModal } from "modules/FactoryExportsDashboard/components";
import "modules/FactoryExportsDashboard/components/FactoryExportsTable/FactoryExportsTable.scss";
import { useExportDataMutation, useExportsQuery } from "modules/FactoryExportsDashboard/components/FactoryExportsTable/hooks";
import { getExportDayName } from "modules/FactoryExportsDashboard/utils";
import { enumToDropdownOptions } from "utils/common";

import type { FactoryExport } from "models";

type FactoryExportsTableProps = {
  onOpenEditExportModal: (factoryExport: FactoryExport) => void;
};

const columnHelper = createColumnHelper<FactoryExport>();

export const FactoryExportsTable = ({ onOpenEditExportModal }: FactoryExportsTableProps) => {
  const { t } = useTranslation();

  const [exportForLog, setExportForLog] = useState<FactoryExport | null>(null);
  const [exportToDelete, setExportToDelete] = useState<FactoryExport | null>(null);

  const { data: exports, isFetching: isExportsFetching, refetch: refetchExports } = useExportsQuery();
  const { mutate: exportData, isPending: isExportDataPending, variables: exportVariables } = useExportDataMutation();
  useRegisterNavAction(() => refetchExports());

  const frequencyOptions = enumToDropdownOptions({ enumObj: FACTORYEXPORT_FREQUENCY, t });
  const queryRangeOptions = enumToDropdownOptions({ enumObj: FACTORYEXPORT_QUERYRANGE, t });

  const getFrequencyName = (frequency: FACTORYEXPORT_FREQUENCY) => frequencyOptions.find(({ value }) => value === frequency)?.text ?? "";
  const getQueryRangeName = (queryRange: FACTORYEXPORT_QUERYRANGE) => queryRangeOptions.find(({ value }) => value === queryRange)?.text ?? "";

  const handleDownloadExport = (factoryExport: FactoryExport) =>
    exportData({ factory_export_id: factoryExport.id }, { onSuccess: url => saveAs(url, `Claire_${factoryExport.name}.${factoryExport.is_ods_format ? "ods" : "csv"}`) });

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        size: 200,
        header: () => <span className="no-wrap-text">{t("v8_export_name", "Export name")}</span>,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor("factory_report_name", {
        id: "factory_report_name",
        size: 200,
        header: () => <span className="no-wrap-text">{t("v8_report_name", "Report name")}</span>,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor("emails", {
        id: "emails",
        size: 300,
        header: () => <span className="no-wrap-text">{t("v8_recipients_emails", "Recipient's emails")}</span>,
        cell: cell => cell.getValue()
      }),
      columnHelper.accessor("frequency", {
        id: "frequency",
        size: 140,
        header: () => <span className="no-wrap-text">{t("v8_frequency", "Frequency")}</span>,
        cell: cell => getFrequencyName(cell.getValue())
      }),
      columnHelper.accessor(row => row, {
        id: "day",
        size: 120,
        header: () => <span className="no-wrap-text capitalize">{t("v8_day", "Day")}</span>,
        cell: ({ row: { original } }) => getExportDayName({ day: original.day, frequency: original.frequency }, t)
      }),
      columnHelper.accessor("query_range", {
        id: "query_range",
        size: 140,
        header: () => <span className="no-wrap-text">{t("v8_query_range", "Query range")}</span>,
        cell: cell => getQueryRangeName(cell.getValue())
      }),
      columnHelper.accessor(row => row, {
        id: "actions",
        size: 160,
        header: () => null,
        cell: ({ row: { original: factoryExport } }) => {
          const isExporting = isExportDataPending && exportVariables.factory_export_id === factoryExport.id;

          return (
            <div className="actions-container">
              <div className="action-button-container" onClick={() => setExportForLog(factoryExport)}>
                <Icon className="rectangle list action-button-icon-center" color="green" />
              </div>
              <Button basic icon disabled={isExporting} loading={isExporting} onClick={() => handleDownloadExport(factoryExport)}>
                <Icon className="download download-icon" color="green" />
              </Button>
              <Can I="update" the="factory-export">
                <div className="action-edit-button-container" onClick={() => onOpenEditExportModal(factoryExport)}>
                  <Icon className="icon pen to square edit-icon action-button-icon-center" />
                </div>
              </Can>
              <Can I="delete" the="factory-export">
                <div className="action-button-container" onClick={() => setExportToDelete(factoryExport)}>
                  <Icon className="trash action-button-icon-center" color="red" />
                </div>
              </Can>
            </div>
          );
        }
      })
    ],
    [t, isExportDataPending, exportVariables]
  );

  return (
    <>
      <div className="FactoryExportsTable">
        <ReactTable isFetching={isExportsFetching} columns={columns} data={exports} />
      </div>
      <ExportLogModal factoryExport={exportForLog} onClose={() => setExportForLog(null)} />
      <DeleteExportModal factoryExport={exportToDelete} onClose={() => setExportToDelete(null)} />
    </>
  );
};
